import { useEffect, useMemo, useRef, useState } from "react";
import type { CostCenter, FixedCost } from "../types.ts";
import { isNameAvailable } from "../names.ts";
import { Modal } from "./Modal.tsx";

type Props = {
  costCenters: CostCenter[];
  fixedCosts: FixedCost[];
  onAdd: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
};

function NameInput({
  center,
  costCenters,
  onRename,
}: {
  center: CostCenter;
  costCenters: CostCenter[];
  onRename: (id: string, name: string) => void;
}) {
  const [draft, setDraft] = useState(center.name);
  const valid = isNameAvailable(draft, costCenters, center.id);

  return (
    <input
      type="text"
      aria-label={`Namn på ${center.name}`}
      aria-invalid={!valid}
      value={draft}
      onChange={(e) => {
        const next = e.currentTarget.value;
        setDraft(next);
        if (isNameAvailable(next, costCenters, center.id)) {
          onRename(center.id, next.trim());
        }
      }}
      onBlur={() => setDraft(center.name)}
    />
  );
}

export function CostCentersDialog({
  costCenters,
  fixedCosts,
  onAdd,
  onRename,
  onDelete,
  onClose,
}: Props) {
  const [draft, setDraft] = useState("");
  const newRef = useRef<HTMLInputElement>(null);

  const usage = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of fixedCosts) {
      if (c.costCenterId) {
        counts.set(c.costCenterId, (counts.get(c.costCenterId) ?? 0) + 1);
      }
    }
    return counts;
  }, [fixedCosts]);

  useEffect(() => {
    newRef.current?.focus();
  }, []);

  const canAdd = isNameAvailable(draft, costCenters);

  const submit = () => {
    if (!canAdd) return;
    onAdd(draft.trim());
    setDraft("");
  };

  return (
    <Modal labelledBy="cost-centers-title" onClose={onClose}>
      <h2 id="cost-centers-title">Kostnadsställen</h2>
      <p className="modal-subtitle">
        Avdelning, projekt eller bolag som en kostnad hör till.
      </p>

      {costCenters.length > 0 && (
        <ul className="cost-center-list">
          {costCenters.map((c) => {
            const used = usage.get(c.id) ?? 0;
            return (
              <li key={c.id}>
                <NameInput
                  center={c}
                  costCenters={costCenters}
                  onRename={onRename}
                />
                <span className="cost-center-usage">
                  {used} kostnad{used === 1 ? "" : "er"}
                </span>
                <button
                  type="button"
                  className="delete-btn"
                  title={`Ta bort ${c.name}`}
                  onClick={() => {
                    onDelete(c.id);
                    newRef.current?.focus();
                  }}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <form
        className="cost-center-new"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          ref={newRef}
          type="text"
          placeholder="Nytt kostnadsställe"
          aria-label="Nytt kostnadsställe"
          aria-invalid={draft.trim() !== "" && !canAdd}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
        />
        <button type="submit" disabled={!canAdd}>
          Lägg till
        </button>
      </form>

      <div className="modal-actions">
        <button type="button" className="primary" onClick={onClose}>
          Stäng
        </button>
      </div>
    </Modal>
  );
}
